import { useMemo } from 'react';

import { useCacheQuery } from '../../../hooks/use-cache-query';
import {
  GetHealthProfessionalItemsDocument,
  GetHealthProfessionalItemsQueryVariables,
  HealthProfessionalItemDataFragment,
  HealthProfessionalStatus,
  SortEnumType,
} from '../../types/api/graphql';
import { AllPracticesId } from '../types';

const getStatuses = (statuses: (HealthProfessionalStatus | 'ALL')[]) => {
  if (statuses.includes('ALL')) {
    return [
      HealthProfessionalStatus.Active,
      HealthProfessionalStatus.Inactive,
      HealthProfessionalStatus.Pending,
    ];
  }

  return statuses as HealthProfessionalStatus[];
};

export function useHealthProfessionalItems(
  variables: Omit<GetHealthProfessionalItemsQueryVariables, 'statuses'> & {
    statuses?: (HealthProfessionalStatus | 'ALL')[];
  },
  skip?: boolean
) {
  const {
    data: healthProfessionalItemsData,
    loading: healthProfessionalItemsLoading,
  } = useCacheQuery(GetHealthProfessionalItemsDocument, {
    variables: {
      ...variables,
      healthProviderId:
        variables.healthProviderId !== AllPracticesId
          ? variables.healthProviderId
          : null,
      statuses: getStatuses(
        variables.statuses ?? [HealthProfessionalStatus.Active]
      ),
      sort: variables.sort ?? [
        { firstName: SortEnumType.Asc },
        { lastName: SortEnumType.Asc },
      ],
    },
    skip: skip === true || !variables.healthProviderId,
  });

  // Subscriptions

  const data = useMemo(() => {
    if (healthProfessionalItemsData?.healthProfessionals) {
      return {
        ...healthProfessionalItemsData.healthProfessionals,
        items: (
          healthProfessionalItemsData.healthProfessionals.items ?? []
        ).map(option => option as HealthProfessionalItemDataFragment),
      };
    }

    return null;
  }, [healthProfessionalItemsData?.healthProfessionals]);

  return {
    data,
    loading: healthProfessionalItemsLoading,
  };
}
